"use client";

import React from "react";
import { ArrowRight, Clock, MapPin, PhoneCall, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const coverageAreas = [{
  name: "Fairfax County"
}, {
  name: "Arlington"
}, {
  name: "Alexandria"
}, {
  name: "Loudoun"
}, {
  name: "Prince William"
}];
const serviceLinks = [{
  name: "Water & Flood",
  to: "/WaterDamageFloodRestorationPage"
}, {
  name: "Mold Remediation",
  to: "/MoldRemediationPage"
}, {
  name: "Fire & Smoke",
  to: "/FireSmokeOdorRestorationPage"
}];
export default function NorthernVirginia() {
  const shouldReduceMotion = useReducedMotion();
  const entranceProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="heronorthernvirginia" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 md:py-28 lg:py-32">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/northern_virginia_restoration_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/northern_virginia_restoration_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/northern_virginia_restoration_hero.png-wvc-sizes" alt="TriServe emergency restoration vehicles and dispatched team in Northern Virginia" loading="eager" className="h-full w-full object-cover object-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/80 to-secondary/60" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div {...entranceProps} className="max-w-3xl space-y-6">
          <div className="inline-flex items-center gap-2">
            <Badge variant="outline" className="border-accent/40 bg-accent/15 text-accent-foreground rounded-full px-3 py-1 font-medium tracking-normal">
              <MapPin className="size-3.5 text-accent" />
              <span>Northern Virginia regional dispatch</span>
            </Badge>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-secondary-foreground">
            Restoration services in Northern Virginia
          </h1>

          <p className="text-lg sm:text-xl text-secondary-foreground/80 leading-relaxed font-sans max-w-2xl">
            24/7 local emergency response across Northern Virginia. Local crews
            coordinate cleanup, direct structural drying, and insurance billing
            with zero call centers.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-medium shadow-sm transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request immediate dispatch</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground bg-secondary/40 hover:bg-secondary-foreground/10 hover:text-secondary-foreground rounded-md transition-[color,background-color,border-color,box-shadow]">
              <a href="tel:8005550199" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4 text-accent" />
                <span>{"Call 24/7 Emergency"}</span>
              </a>
            </Button>
          </div>

          <div className="pt-8 border-t border-secondary-foreground/15 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-secondary-foreground/85">
            <div className="flex items-center gap-2.5">
              <Clock className="size-4 text-accent shrink-0" />
              <span>On-site in 60 minutes or less</span>
            </div>
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="size-4 text-accent shrink-0" />
              <span>Direct insurance claims processing</span>
            </div>
            <div className="flex items-center gap-2.5 sm:col-span-2 lg:col-span-1">
              <MapPin className="size-4 text-accent shrink-0" />
              <span>Certified local technicians</span>
            </div>
          </div>

          <div className="pt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-secondary-foreground/60 font-semibold">
              Emergency coverage:
            </span>
            {coverageAreas.map((area, index) => <span key={area.name} data-index={index} className="text-xs px-2.5 py-1 rounded-sm bg-secondary-foreground/10 text-secondary-foreground/90 border border-secondary-foreground/10">
                {area.name}
              </span>)}
          </div>

          <div className="pt-2 flex flex-wrap items-center gap-4 text-xs text-secondary-foreground/75">
            <span className="text-secondary-foreground/60">Restoration options:</span>
            {serviceLinks.map((item, index) => <Link key={item.name} to={item.to} data-index={index} className="underline underline-offset-4 hover:text-secondary-foreground transition-colors">
                {item.name}
              </Link>)}
          </div>
        </motion.div>
      </div>
    </section>;
}